import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from "classnames";
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { config } from 'node-config-ts';
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import { isStringTrue } from '@iddataweb/idw-common-ui/utils';
import { apiRoutes } from '@constants/api-routes';
import { resources } from '@constants/hardcoded-strings';
import { DASHBOARD_URL } from '@constants/routes';
import LogoWeb from "@content/images/logo-web.png"
import GoogleLogo from "@content/images/google-logo.png"
import { InputFormGroup } from '@containers/common/input-form-group/input-form-group';
import { ButtonTypes } from "@enums/button-types";
import { LoginErrorCodeTypes } from '@enums/login-error-code-types';
import { setSession } from '@redux/actions/session-actions';
import { getSessionReduxRequest } from '@redux-requests/session';
import { authenticateUserRequest, getSessionRequest } from "@requests/axnadmin-core";
import { logAndDisplayErrorModal } from '@utils/app-alert-util';
import { getHostnameWithScheme } from '@utils/http-util';
import { navigateToUtil } from '@utils/navigation-util';
import { LoadingIndicator } from '../common/loading-indicator/loading-indicator';
import { ButtonWithSpinner } from '../common/button-with-spinner/button-with-spinner';
import styles from './login-container.less';
import { getSessionState } from '@redux/selectors/root-selectors';

type LoginContainerState = {
    isRedirectingToCompany: boolean;
    isRedirecting: boolean;
    email: string;
    errorCode: string;
};

const initialState: LoginContainerState = {
    isRedirectingToCompany: false,
    isRedirecting: false,
    email: "",
    errorCode: null
};

export const LoginContainer = () => {
    const fixedLogoWeb = LogoWeb.split("assets").join("/assets");
    const fixedGoogleLogo = GoogleLogo.split("assets").join("/assets");

    const dispatch = useDispatch();

    const { isLoading: isLoadingSession, session } = useSelector(getSessionState);

    const [state, setState] = useState<LoginContainerState>(initialState);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const routerLocation = useLocation();

    const attemptToGetSession = async () => {
        const { data } = await getSessionRequest();
        if (data) {
            dispatch(setSession(data));
        }
    };

    const authenticateUser = async (email: string, idp: string) => {
        const invitationToken = searchParams.get("invitationToken");

        const authenticateUserResponse = await authenticateUserRequest(
            email,
            invitationToken,
            idp
        );

        if (authenticateUserResponse.data?.token) {
            const REDIRECT_URL = `${getHostnameWithScheme()}${apiRoutes.axnadminCore.authenticateUser}`;
            window.location.href = `${REDIRECT_URL}?token=${authenticateUserResponse.data.token}`;
        } else if (authenticateUserResponse.error) {
            logAndDisplayErrorModal(
                dispatch,
                authenticateUserResponse.error,
                resources.requests.authenticateUser.error.title,
                resources.requests.authenticateUser.error.message
            );
            setState({ ...state, errorCode: null, isRedirecting: false, isRedirectingToCompany: false });
        }
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setState({ ...state, errorCode: null, isRedirecting: true, isRedirectingToCompany: true });
        authenticateUser(state.email, null);
    };

    const handleCorporateEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
        setState({ ...state, email: event.target.value });
    };

    const handleGoogleLogin = async () => {
        setState({ ...state, errorCode: null, isRedirecting: true, isRedirectingToCompany: false });
        authenticateUser(null, "google");
    };

    useEffect(() => {
        if (session) {
            const savedRedirectUrl = localStorage.getItem("redirectUrl");
            if (savedRedirectUrl) {
                localStorage.removeItem("redirectUrl");
                navigateToUtil(savedRedirectUrl, navigate, routerLocation.pathname);
            } else {
                navigateToUtil(DASHBOARD_URL, navigate, routerLocation.pathname);
            }
        }
    }, [session]);

    useEffect(() => {
        setTimeout(() => {
            const input = document.getElementById("corporate-login-email-input");
            if (input) {
                input.setAttribute("autocomplete", "on");
            }
        }, 500);
    }, []);

    useEffect(() => {
        if (Array.from(searchParams.entries()).length > 0) {
            const errorCode = searchParams.get("errorCode");
            if (errorCode) {
                setState({
                    ...state,
                    errorCode,
                    isRedirecting: false,
                    isRedirectingToCompany: false
                });
            } else {
                const authenticated = searchParams.get("authenticated");
                if (authenticated && isStringTrue(authenticated)) {
                    getSessionReduxRequest(dispatch)(routerLocation, navigate);
                }
            }
        } else if (config.APPLICATION_ENVIRONMENT !== "local") {
            attemptToGetSession();
        }
    }, [searchParams]);

    const errorMessage = useMemo(() => {
        if (state.errorCode) {
            switch (state.errorCode) {
                case LoginErrorCodeTypes.USER_NOT_FOUND:
                    return resources.pages.login.errors.userNotFound;
                case LoginErrorCodeTypes.SECURITY_CHECK_FAILURE:
                    return resources.pages.login.errors.securityCheckFailure;
                case LoginErrorCodeTypes.ORG_LOGIN_NOT_SUPPORTED:
                    return resources.pages.login.errors.orgLoginNotSupported;
                case LoginErrorCodeTypes.INVITATION_EXPIRED:
                    return resources.pages.login.errors.invitationExpired;
                case LoginErrorCodeTypes.INVITATION_INVALID:
                    return resources.pages.login.errors.invitationInvalid;
                case LoginErrorCodeTypes.INVITATION_USED:
                    return resources.pages.login.errors.invitationUsed;
                case LoginErrorCodeTypes.SESSION_TIMEOUT:
                    return resources.pages.login.errors.sessionTimeout;
                default:
                    return resources.pages.login.errors.loginFailure;
            }
        }

        return null;
    }, [state.errorCode]);

    return (
        <div className={styles.loginContainer}>
            <div className={styles.loginContainerContent}>
                {!isLoadingSession && (
                    <>
                        <div className={styles.loginContainerHeader}>
                            <a id={`iddataweb-header-logo`} className={styles.iconAnchor}>
                                <img src={fixedLogoWeb} />
                            </a>
                            <h3 className={styles.loginWelcomeText}>{resources.pages.login.welcomeText}</h3>
                            {errorMessage && (
                                <Alert className={styles.errorAlert} variant={"danger"}>
                                    {errorMessage} <a rel={"noreferrer"} target={"_blank"} href={"https://docs.iddataweb.com/docs/axn-admin-login-issues"}>{resources.pages.login.moreInfo}</a>
                                </Alert>
                            )}
                        </div>
                        <Form onSubmit={handleSubmit} className={styles.loginContainerForm}>
                            <div className={styles.corporateInputContainer}>
                                <InputFormGroup
                                    controlId={"corporate-login-email-input"}
                                    className={styles.corporateInput}
                                    label={null}
                                    placeholder={resources.pages.login.corporateEmailButtonLabel}
                                    value={state.email}
                                    type={"text"}
                                    autoComplete={"on"}
                                    onChange={handleCorporateEmailChange}
                                    isRequired={true}
                                    disabled={state.isRedirecting}
                                />
                                <ButtonWithSpinner
                                    variant="primary"
                                    id={"corporate-login-button"}
                                    type={ButtonTypes.SUBMIT}
                                    className={styles.corporateLoginButton}
                                    label={resources.pages.login.corporateEmailButtonText}
                                    disabled={state.isRedirecting}
                                    hideSpinner={state.isRedirecting && !state.isRedirectingToCompany}
                                />
                            </div>
                        </Form>
                        <div className={styles.divider}>
                            <span />
                            <span>OR</span>
                            <span />
                        </div>
                        <div className={styles.googleSso}>
                            <Button
                                id={"google-login-button"}
                                className={classNames(styles.googleSsoButton, state.isRedirecting ? styles.loadingButton : "")}
                                disabled={state.isRedirecting}
                                onClick={handleGoogleLogin}>
                                <img src={fixedGoogleLogo} />
                                {resources.pages.login.googleLoginButton}
                                {state.isRedirecting && !state.isRedirectingToCompany && (
                                    <Spinner
                                        className={styles.spinner}
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                    />
                                )}
                            </Button>
                        </div>
                    </>
                )}
                {isLoadingSession && (
                    <div className={styles.appLoading}>
                        <LoadingIndicator title={resources.pages.login.authenticating} />
                    </div>
                )}
            </div>
            <div className={styles.loginContainerFooter}>
                <span className={styles.copyright}><a>&#169; {resources.pages.login.copyright(`${new Date().getFullYear()}`)}</a></span>
            </div>
        </div>
    );
}